import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, TextField as MuiTextField, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, Typography, Card, CardContent
} from '@material-ui/core';
import { useNotify, ReferenceArrayInput, AutocompleteArrayInput, Create, SimpleForm, Toolbar } from 'react-admin';
import { useForm, useFormState } from 'react-final-form';
import { SectionHeader, apiRequest } from './utils';

const POLL_INTERVAL = 4000;
// Form field holding the selected site codes for a new purge job. It lives in
// this page's own isolated form (not any persisted record) and is reset after
// a job is created.
const SITE_SELECT_SOURCE = 'purge_site_codes';
const STATUS_COLORS = {
  awaiting_rewrite_confirmation: 'secondary',
  verified: 'secondary',
  failed: 'secondary',
  completed: 'primary'
};

export const TypeToConfirmDialog = ({ open, title, description, onConfirm, onClose }) => {
  const [text, setText] = useState('');
  const handleClose = () => { setText(''); onClose(); };
  const handleConfirm = () => { setText(''); onConfirm(); };
  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{description}</DialogContentText>
        <DialogContentText color="error">
          This action is irreversible. The Spaces archive has no versioning.
        </DialogContentText>
        <MuiTextField
          id="purge-confirm-input"
          label="Type PURGE to confirm"
          value={text}
          onChange={e => setText(e.target.value)}
          fullWidth
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleConfirm} disabled={text !== 'PURGE'} color="secondary" variant="contained">
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Built-in react-admin site multi-select, matching the pattern used across the
// app (e.g. adquality_reports.js). Rendered inside this page's own SimpleForm.
const SitePicker = () => (
  <ReferenceArrayInput
    source={SITE_SELECT_SOURCE}
    reference="sites"
    basePath="sites"
    label="Sites to purge"
    perPage={15}
    sort={{ field: 'name', order: 'ASC' }}
    filterToQuery={searchText => ({ search_name: searchText })}
    style={{ minWidth: 400 }}
  >
    <AutocompleteArrayInput
      optionText={record => (record && record.name) ? `${record.name} (${record.code})` : ''}
      optionValue="code"
    />
  </ReferenceArrayInput>
);

function describeGate (job) {
  if (job.status === 'awaiting_rewrite_confirmation' && job.summary) {
    return `Rewrite ${job.summary.files} file(s) containing ${job.summary.matched_lines} matching line(s) ` +
      `(adserver: ${job.summary.per_archive.adserver}, carbon: ${job.summary.per_archive.carbon}). ` +
      'Originals are backed up in quarantine.';
  }
  if (job.status === 'verified' && job.summary) {
    return `Verification passed (${job.summary.total_removed} line(s) removed). ` +
      'Delete the quarantine backups? Erasure is not complete until backups are purged.';
  }
  return '';
}

export const DataPurgeList = (props) => {
  const notify = useNotify();
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // { job, action, title }
  const pollRef = useRef(null);
  const mountedRef = useRef(true);

  const loadJobs = useCallback(async () => {
    try {
      const list = await apiRequest('/site_purge_jobs');
      if (mountedRef.current) setJobs(list);
      return list;
    } catch (e) {
      return [];
    }
  }, []);

  useEffect(() => {
    loadJobs();
    pollRef.current = setInterval(() => { loadJobs(); }, POLL_INTERVAL);
    return () => { mountedRef.current = false; clearInterval(pollRef.current); };
  }, [loadJobs]);

  const runAction = async (job, action) => {
    try {
      await apiRequest(`/site_purge_jobs/${job.code}/${action}`, { method: 'POST' });
      notify(`Action '${action}' accepted`, 'info');
      await loadJobs();
    } catch (e) {
      notify(`Action failed: ${e.message}`, 'warning');
    }
  };

  const progress = (job) => {
    if (job.status === 'locating' && job.phases && job.phases.locate) {
      return `${job.phases.locate.months_done}/${job.phases.locate.months_total} months`;
    }
    return '';
  };

  // Custom SimpleForm toolbar: no SaveButton. The only action is starting a
  // purge job, which reads the selected sites from this page's own form.
  const StartPurgeToolbar = (toolbarProps) => {
    const form = useForm();
    const { values } = useFormState();
    const codes = values[SITE_SELECT_SOURCE] || [];
    const startJob = async () => {
      setBusy(true);
      try {
        await apiRequest('/site_purge_jobs', { method: 'POST', body: { site_codes: codes } });
        notify('Purge job created', 'info');
        form.change(SITE_SELECT_SOURCE, undefined);
        await loadJobs();
      } catch (e) {
        notify(`Failed to create purge job: ${e.message}`, 'warning');
      } finally {
        setBusy(false);
      }
    };
    return (
      <Toolbar {...toolbarProps}>
        <Button variant="contained" color="primary" disabled={busy || codes.length === 0} onClick={startJob}>
          {busy ? <CircularProgress size={18} /> : 'Start Purge Job'}
        </Button>
      </Toolbar>
    );
  };

  return (
    <>
      <Create title="Data Purge" {...props}>
        <SimpleForm toolbar={<StartPurgeToolbar />}>
          <SectionHeader title="Purge Raw Site Data From Spaces Archives" />
          <Typography variant="body2" style={{ marginBottom: 12 }}>
            Removes every raw log line for the selected sites from the adserver and carbon Spaces archives.
            Aggregated/derived data is not touched. Two confirmations are required: before rewriting files,
            and before deleting the quarantine backups.
          </Typography>
          <SitePicker />
        </SimpleForm>
      </Create>

      <Card style={{ marginTop: 16 }}>
        <CardContent>
          <SectionHeader title="Purge Jobs" />
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Sites</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Matched Lines</TableCell>
                <TableCell>Requested By</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map(job => (
                <TableRow key={job.code}>
                  <TableCell>{job.code}</TableCell>
                  <TableCell>{(job.site_names || []).join(', ')}</TableCell>
                  <TableCell>
                    <Chip size="small" label={job.status} color={STATUS_COLORS[job.status] || 'default'} />
                    {job.error ? <div style={{ color: '#f44336', fontSize: 12 }}>{job.error.message}</div> : null}
                  </TableCell>
                  <TableCell>{progress(job)}</TableCell>
                  <TableCell>{job.summary ? job.summary.matched_lines : ''}</TableCell>
                  <TableCell>{job.requested_by ? job.requested_by.username : ''}</TableCell>
                  <TableCell>
                    {job.status === 'awaiting_rewrite_confirmation' &&
                      <Button size="small" color="secondary" variant="contained"
                        onClick={() => setDialog({ job, action: 'confirm_rewrite', title: 'Confirm Rewrite' })}>
                        Confirm Rewrite
                      </Button>}
                    {job.status === 'verified' &&
                      <Button size="small" color="secondary" variant="contained"
                        onClick={() => setDialog({ job, action: 'confirm_purge', title: 'Purge Backups' })}>
                        Purge Backups
                      </Button>}
                    {job.status === 'failed' &&
                      <Button size="small" onClick={() => runAction(job, 'retry')}>Retry</Button>}
                    {['created', 'scoping', 'locating', 'backing_up', 'awaiting_rewrite_confirmation'].includes(job.status) &&
                      <Button size="small" onClick={() => runAction(job, 'cancel')}>Cancel</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <TypeToConfirmDialog
        open={dialog !== null}
        title={dialog ? dialog.title : ''}
        description={dialog ? describeGate(dialog.job) : ''}
        onClose={() => setDialog(null)}
        onConfirm={async () => { const d = dialog; setDialog(null); await runAction(d.job, d.action); }}
      />
    </>
  );
};

export default DataPurgeList;
